import CircularProgress, {
  circularProgressClasses,
} from "@mui/material/CircularProgress";
import Typography, { TypographyProps } from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { blue, neutral } from "@flat-ui";
import { useMemo } from "react";

interface Props {
  /** Progress value to show inside the spinner */
  value?: number;

  /** Max value. If not set the default is 100 */
  maxValue?: number;

  /** Text to show under the spinner */
  label?: string;

  /** If passed the default percentage unit will be replaced by the given unit */
  unit?: string;

  /** If true the value will not be displayed in the center */
  shouldHideValue?: boolean;

  /** Size of the circular progress */
  size?: number;

  /** Thickness of the circular progress */
  thickness?: number;

  /** Optional sx properties to override the value style. */
  sxValue?: TypographyProps;

  /**
   * Styling of the ends of the progress curve. Provide "square" for simple square endings.
   * @default "round"
   */
  strokeLinecap?: "round" | "square";
}

/** Default size (height and width) of the circular progress in pixels */
const CIRCULAR_PROGRESS_SIZE = 60;

/** Default thickness for the circular progress, it is unitless and refers to CircularProgress thickness */
const CIRCULAR_PROGRESS_THICKNESS = 4;

/** Render a circular progress spinner with progress value if provided */
export function SphereCircularProgress({
  value,
  maxValue,
  label,
  unit,
  shouldHideValue,
  size,
  thickness,
  sxValue,
  strokeLinecap = "round",
}: Props): JSX.Element {
  const circleSize = useMemo(() => {
    return `${size ?? CIRCULAR_PROGRESS_SIZE}px`;
  }, [size]);

  const indicatorThickness = useMemo(() => {
    return thickness ?? CIRCULAR_PROGRESS_THICKNESS;
  }, [thickness]);

  const indicatorUnit = useMemo(() => {
    return typeof unit === "string" ? unit : "%";
  }, [unit]);

  const indicatorValue = useMemo(() => {
    if (value === undefined) {
      return undefined;
    }
    return value > 0 ? value : 0;
  }, [value]);

  const progress = useMemo(() => {
    if (indicatorValue === undefined) {
      return;
    }

    const indicatorMaxValue = maxValue ?? 100;

    if (indicatorValue > indicatorMaxValue) {
      return 100;
    }

    return (indicatorValue / indicatorMaxValue) * 100;
  }, [maxValue, indicatorValue]);

  return (
    <Box data-testid="sphere-circular-progress">
      <Box
        sx={{
          position: "relative",
          height: circleSize,
          width: circleSize,
        }}
      >
        <CircularProgress
          variant="determinate"
          sx={{
            color: neutral[200],
            position: "absolute",
            left: 0,
          }}
          size={circleSize}
          thickness={indicatorThickness}
          value={100}
        />
        <CircularProgress
          variant={
            typeof indicatorValue !== "undefined"
              ? "determinate"
              : "indeterminate"
          }
          sx={{
            color: blue[500],
            position: "absolute",
            left: 0,
            [`& .${circularProgressClasses.circle}`]: {
              // There seems to be a bug in MUI where providing "square" instead of undefined overpaints the filled
              // area so that e.g. the circle is already full at 97%.
              strokeLinecap: strokeLinecap === "round" ? "round" : undefined,
            },
          }}
          size={circleSize}
          thickness={indicatorThickness}
          value={progress}
        />
        {typeof indicatorValue !== "undefined" && !shouldHideValue && (
          <Box
            data-testid="sphere-circular-progress-value"
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{ ...sxValue, color: neutral[800] }}
            >{`${Math.round(indicatorValue)}${indicatorUnit}`}</Typography>
          </Box>
        )}
      </Box>

      {label && (
        <Typography
          data-testid="sphere-circular-progress=label"
          variant="caption"
          component="div"
          sx={{ paddingTop: "10px", color: neutral[800] }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}
